import PropTypes from 'prop-types'
import React, { Component } from 'react'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import * as GateSelectors from 'app/modules/gates/selectors'
import * as OrgSelectors from 'app/modules/org/selectors'
import * as OrgActions from 'app/modules/org/actions'
import * as InboxActions from 'app/modules/inbox/actions'
import * as ConversationActions from 'app/modules/conversation/actions'
import * as EmbedActions from 'app/modules/embed/actions'
import * as EmbedSelectors from 'app/modules/embed/selectors'
import { inboxes } from 'app/modules/inbox/selectors'

import { variation } from 'app/modules/experiment/helpers.js'
import { ensureIsAdmin } from 'app/modules/access/helpers'
import { patchOperations } from 'utils/PatchHelper'

import PageTitle from '../ChatWidget/components/PageTitle'
import BackButton from '../ChatWidget/components/BackButton'
import ToggleSection from '../ChatWidget/components/Advanced/components/ToggleSection'

import AutoCloseConfiguration from './AutoCloseConfiguration'
import CustomSequenceLimitModal from './CustomSequenceLimitModal'

import style from './style.styl'

const SEQUENCE_LIMIT = 150

class ConversationsSettings extends Component {
  static contextTypes = {
    app: PropTypes.object.isRequired,
    router: PropTypes.object.isRequired,
  }

  constructor(props) {
    super(props)

    this.state = {
      startDate: null,
      endDate: null,
      focusedInput: null,
      selectedInbox: null,
      limitNumChats: props.orgProperties['limitNumChats'] || 0,
      orgSequenceLimit: props.orgProperties['orgSequenceLimit'] || SEQUENCE_LIMIT,
      limitNumChatsEnabled: props.orgProperties['limitNumChatsEnabled'] || false,
      rerouteConversationsEnabled: props.orgProperties['rerouteConversationsEnabled'] || false,
      customOrgSequenceEnabled: props.orgProperties['customOrgSequenceEnabled'] || false,
      showUnfurl: _.get(props.embed, ['theme', 'showUnfurl'], true),
      updatePending: false,
      sequenceLimitError: false,
      showCustomSequenceLimitWarning: false,
    }
  }

  componentDidMount() {
    this.props.actions.fetchInboxList()
  }

  componentWillReceiveProps(nextProps) {
    const updated =
      this.props.orgProperties['limitNumChats'] !== nextProps.orgProperties['limitNumChats'] ||
      this.props.orgProperties['limitNumChatsEnabled'] !== nextProps.orgProperties['limitNumChatsEnabled'] ||
      this.props.orgProperties['rerouteConversationsEnabled'] !==
        nextProps.orgProperties['rerouteConversationsEnabled'] ||
      this.props.orgProperties['customOrgSequenceEnabled'] !==
        nextProps.orgProperties['customOrgSequenceEnabled'] ||
      this.props.orgProperties['orgSequenceLimit'] !== nextProps.orgProperties['orgSequenceLimit'] ||
      _.get(this.props.embed, ['theme', 'showUnfurl']) !== _.get(nextProps.embed, ['theme', 'showUnfurl'])

    if (updated) {
      if (this.state.updatePending) {
        this.notifySuccess()
      }
      this.setState({
        limitNumChats: nextProps.orgProperties['limitNumChats'],
        limitNumChatsEnabled: nextProps.orgProperties['limitNumChatsEnabled'],
        rerouteConversationsEnabled: nextProps.orgProperties['rerouteConversationsEnabled'],
        customOrgSequenceEnabled: nextProps.orgProperties['customOrgSequenceEnabled'],
        orgSequenceLimit: nextProps.orgProperties['orgSequenceLimit'],
        showUnfurl: _.get(nextProps.embed, ['theme', 'showUnfurl']),
        updatePending: false,
      })
    }
  }

  hasGlobalAutoClose = () => variation('has_global_auto_close') === 'on'

  handleDateChange = ({ startDate, endDate }) => {
    this.setState({
      startDate: startDate,
      endDate: endDate,
    })
  }

  debouncedHandleChatLimitChange = _.debounce(limitNumChats => {
    const { limitNumChatsEnabled } = this.state
    this.setState({
      updatePending: true,
    })

    this.props.actions.setConversationLimit({ limitNumChatsEnabled, limitNumChats })
  }, 1000)

  debouncedHandleSequenceLimitChange = _.debounce(value => {
    const ops = patchOperations(
      {
        ['orgSequenceLimit']: value || String(SEQUENCE_LIMIT),
        ['customOrgSequenceEnabled']: true,
      },
      this.props.orgProperties
    )

    if (ops.length > 0) {
      this.setState({
        updatePending: true,
      })
      this.props.actions.patchOrgProperties(ops)
    }
  }, 1000)

  handleChatLimitChange = event => {
    this.setState(
      {
        limitNumChats: parseInt(event.target.value) || undefined,
      },
      () => {
        this.debouncedHandleChatLimitChange(this.state.limitNumChats)
      }
    )
  }

  handleOrgSequenceLimitChange = event => {
    this.setState(
      {
        orgSequenceLimit: parseInt(event.target.value) || undefined,
      },
      () => {
        if (this.state.orgSequenceLimit < 2001) {
          this.setState({
            sequenceLimitError: false,
          })
          this.debouncedHandleSequenceLimitChange(String(this.state.orgSequenceLimit))
        } else {
          this.setState({
            sequenceLimitError: true,
          })
        }
      }
    )
  }

  handleChangeCustomSequenceLimitToggle = event => {
    if (event.target.checked) {
      this.setState({
        showCustomSequenceLimitWarning: true,
      })
    } else {
      this.setState(
        {
          customOrgSequenceEnabled: false,
          orgSequenceLimit: SEQUENCE_LIMIT,
          updatePending: true,
          showCustomSequenceLimitWarning: false,
        },
        () => {
          const ops = patchOperations(
            {
              ['customOrgSequenceEnabled']: this.state.customOrgSequenceEnabled,
              ['orgSequenceLimit']: String(SEQUENCE_LIMIT),
            },
            this.props.orgProperties
          )
          this.props.actions.patchOrgProperties(ops)
        }
      )
    }
  }

  handleChangeLimitEnabledToggle = event => {
    this.setState(
      {
        limitNumChatsEnabled: event.target.checked,
        updatePending: true,
      },
      () => {
        const { limitNumChatsEnabled, limitNumChats } = this.state
        this.props.actions.setConversationLimit({ limitNumChatsEnabled, limitNumChats })
      }
    )
  }

  handleChangeRerouteConversationToggle = event => {
    this.setState(
      {
        rerouteConversationsEnabled: event.target.checked,
        updatePending: true,
      },
      () => {
        const ops = patchOperations(
          {
            ['rerouteConversationsEnabled']: this.state.rerouteConversationsEnabled,
          },
          this.props.orgProperties
        )
        this.props.actions.patchOrgProperties(ops)
      }
    )
  }

  handleUnfurlToggle = event => {
    this.setState(
      {
        showUnfurl: event.target.checked,
        updatePending: true,
      },
      () => {
        const ops = [{ operation: "set", property: "theme.showUnfurl", value: this.state.showUnfurl }]
        this.props.actions.patchEmbed(ops)
      }
    )
  }

  handleChatLimitSubmit = event => {
    event.preventDefault()
  }

  selectInbox = value => {
    this.setState({
      selectedInbox: value.value,
    })
  }

  getInboxOptions = () =>
    this.props.inboxes
      .valueSeq()
      .map(inbox => ({
        label: inbox.name,
        value: inbox.id,
      }))
      .toArray()

  acceptCustomSequenceWarning = () => {
    this.setState(
      {
        customOrgSequenceEnabled: true,
        orgSequenceLimit: SEQUENCE_LIMIT,
        updatePending: true,
        showCustomSequenceLimitWarning: false,
      },
      () => {
        const ops = patchOperations(
          {
            ['customOrgSequenceEnabled']: this.state.customOrgSequenceEnabled,
            ['orgSequenceLimit']: String(SEQUENCE_LIMIT),
          },
          this.props.orgProperties
        )
        this.props.actions.patchOrgProperties(ops)
      }
    )
  }

  declineCustomSequenceWarning = () => {
    this.setState({
      showCustomSequenceLimitWarning: false,
    })
  }

  notifySuccess = () => {
    this.context.app.actions.NotificationActions.pushNotification({
      content: 'Your conversations settings were successfully updated.',
      variant: 'green',
    })
  }

  render() {
    const {
      limitNumChats,
      limitNumChatsEnabled,
      rerouteConversationsEnabled,
      showUnfurl,
      orgSequenceLimit,
      sequenceLimitError,
      customOrgSequenceEnabled,
      showCustomSequenceLimitWarning,
    } = this.state

    const {
      hasLimitNumberOfChats,
      hasCompanyPlan,
    } = this.props

    return (
      <div className={style.Conversations}>
        <BackButton title="Conversations Settings" url="/settings/conversations" />

        <PageTitle title="Advanced" description="Customize advanced settings for conversations" />
        <ToggleSection
          title="Unfurl links in the conversation"
          description="Show or hide the preview of links in the conversation view"
          className="advanced-settings-toggle conversation-unfurl-links"
          isChecked={showUnfurl}
          onToggled={this.handleUnfurlToggle}
        />


        {(hasLimitNumberOfChats || hasCompanyPlan) && (
          <ToggleSection
            title="Limit Chats For Your Reps"
            description="When a rep is over their limit of open conversations, Drift will not route any new conversations to them so they can focus on their current ones."
            isChecked={limitNumChatsEnabled}
            className="advanced-settings-toggle"
            onToggled={this.handleChangeLimitEnabledToggle}
          >
            <div className="content">
              <div className="conversation-limit-number-of-chats">
                <span>Automatically set my reps to be away when they have </span>
                <form className="drift-form" onSubmit={this.handleChatLimitSubmit}>
                  <input type="number" min={0} value={limitNumChats} onChange={this.handleChatLimitChange} />
                </form>
                <span> open conversations.</span>
              </div>
            </div>
          </ToggleSection>
        )}
        {hasCompanyPlan && (
          <div>
            <ToggleSection
              title="Reroute conversations with no participants"
              description="When an agent comes online, we will reroute these conversations according to your Lead Routing Rules."
              className="advanced-settings-toggle conversation-reroute-conversations"
              isChecked={rerouteConversationsEnabled}
              onToggled={this.handleChangeRerouteConversationToggle}
            />
          </div>
        )}
        {this.hasGlobalAutoClose() && <AutoCloseConfiguration onChangeSuccess={this.notifySuccess} />}
        {hasCompanyPlan && (
          <div>
            <ToggleSection
              title="Customize Sequence Enroll Limit for your Reps"
              description={`If your reps need more than ${SEQUENCE_LIMIT} sequence enrollments per day, you can update that number here.`}
              isChecked={customOrgSequenceEnabled}
              onToggled={this.handleChangeCustomSequenceLimitToggle}
              className="advanced-settings-toggle conversation-unfurl-links"
            />
            {customOrgSequenceEnabled && (
              <div className="content">
                <div className="limit-number-of-sequences ">
                  <span>Allow my reps to enroll up to </span>
                  <form className="drift-form" onSubmit={this.handleChatLimitSubmit}>
                    <input
                      type="number"
                      min={SEQUENCE_LIMIT}
                      max={2000}
                      value={orgSequenceLimit}
                      onChange={this.handleOrgSequenceLimitChange}
                    />
                  </form>
                  <span> contacts in sequences per day. </span>
                  {sequenceLimitError && (
                    <span className="sequence-limit-error">
                      {' '}
                      Please input a limit between {SEQUENCE_LIMIT} and 2000
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
        {showCustomSequenceLimitWarning && (
          <CustomSequenceLimitModal
            handleCloseModal={this.declineCustomSequenceWarning}
            handleDisconnect={this.acceptCustomSequenceWarning}
          />
        )}
      </div>
    )
  }
}

const mapStateToProps = state => ({
  hasLimitNumberOfChats: GateSelectors.hasLimitNumberOfChats(state),
  hasCompanyPlan: GateSelectors.hasCompanyPlan(state),
  hasNewConvoExport: GateSelectors.hasNewConvoExport(state),
  orgProperties: OrgSelectors.properties(state),
  embed: EmbedSelectors.getEmbedConfig(state),
  inboxes: inboxes(state),
})

const mapDispatchToProps = dispatch => {
  return {
    actions: bindActionCreators(
      {
        fetchInboxList: InboxActions.fetchInboxList,
        patchOrgProperties: OrgActions.patchOrgProperties,
        patchEmbed: EmbedActions.patchEmbed,
        setConversationLimit: ConversationActions.setConversationLimit,
      },
      dispatch
    ),
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(ensureIsAdmin(ConversationsSettings))



// WEBPACK FOOTER //
// ./src/scripts/components/settings/advancedConversationSettings/index.js